import { clsx } from 'clsx'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Post, PostStatus } from '@tribeplatform/gql-client/types'
import { useReplies } from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Link } from '@tribeplatform/react-ui-kit/Link'

import { T } from '../i18n/components/T.js'
import { ReplyBar } from '../Post/ReplyBar.js'
import { Reply } from './Reply.js'

export const ReplyList = ({
  post,
  readonly,
  className,
}: {
  post: Post
  readonly?: boolean
  className?: string
}) => {
  const { data, fetchNextPage, hasNextPage, isFetching, isInitialLoading } =
    useReplies({
      variables: {
        limit: 5,
        postId: post?.id,
        reverse: true,
      },
      fields: {
        owner: {
          member: { profilePicture: 'basic', badges: 'all' },
        },
        embeds: 'basic',
        mentions: 'basic',
        attachments: 'basic',
        authMemberProps: 'all',
        reactions: { fields: 'all', variables: { limit: 10 } },
        replies: {
          fields: {
            owner: {
              member: { profilePicture: 'basic', badges: 'all' },
            },
            embeds: 'basic',
            mentions: 'basic',
            attachments: 'basic',
            authMemberProps: 'all',
            reactions: { fields: 'all', variables: { limit: 10 } },
          },
          variables: {
            limit: 2,
            reverse: true,
          },
        },
      },
      useInfiniteQueryOptions: {
        refetchOnMount: 'always',
        enabled: !!post?.id,
      },
    })

  const [canCreateReply] = hasScopesPermission(post, ['createReply'])
  const canReply =
    !readonly && post && post?.status === PostStatus.PUBLISHED && canCreateReply

  if (isInitialLoading) {
    return (
      <Card className={clsx('mt-5', className)}>
        <Card.Content className="pb-3 sm:pb-3 px-5 flex flex-col space-y-6">
          {[...Array(3)].map((_e, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="animate-pulse flex space-s-3" key={i}>
              <div className="rounded-full h-10 w-10 bg-skeleton " />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-4 bg-skeleton rounded-full w-4/6"></div>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div className="h-4 bg-skeleton rounded-full w-full"></div>
                </div>
              </div>
            </div>
          ))}
        </Card.Content>
      </Card>
    )
  }

  const { nodes: replies } = simplifyPaginatedResult<Post>(data)

  if (!(replies?.length > 0 || canReply)) {
    return null
  }

  return (
    <Card className={clsx('mt-5', className)}>
      <Card.Content className="flex flex-col space-y-6 px-5 empty:hidden">
        {hasNextPage && (
          <div className="flex justify-between">
            <div className="flex items-center space-s-3">
              <Link
                variant="inherit"
                className="text-sm leading-5 font-medium text-content-subdued"
                onClick={() => {
                  fetchNextPage()
                }}
              >
                <T
                  id="Reply.ReplyList.MoreRepliesButtonText"
                  defaultMessage="View more replies"
                />
              </Link>
              {isFetching && <SpinnerIcon className="animate-spin" />}
            </div>
            <div className="text-content-subdued text-sm leading-5 font-normal">
              <T
                id="Reply.ReplyList.LoadedOfTotalRepliesText"
                defaultMessage="{loaded_replies} of {total_replies}"
                values={{
                  loaded_replies: replies.length,
                  total_replies: post?.repliesCount,
                }}
              />
            </div>
          </div>
        )}
        <div className="relative flex flex-col space-y-6">
          {replies?.reverse()?.map(reply => (
            <Reply
              key={reply.id}
              reply={reply}
              post={post}
              canReply
              readonly={readonly}
              context="post"
            />
          ))}
          {canReply && <ReplyBar post={post} />}
        </div>
      </Card.Content>
    </Card>
  )
}
